// PostHog init is deferred to reduce initial load; on Stripe checkout return we init immediately so self_serve_checkout_completed isn't dropped (posthog-js doesn't buffer pre-init captures).
import { AURORA_FLAG_CACHE_KEY } from "./src/dashboard/lib/aurora-flag-cache"
import { getConsoleSurfaceProperties } from "./src/core/lib/console-surface"

// --- console_surface / classic_section super properties (ENG-3125) ---------
//
// PostHog autocapture keys on the raw URL, so a classic page's identity changes
// whenever its URL shape does. Registering the surface as a super property puts
// a stable, URL-independent dimension on every event — autocaptured `$pageview`
// included — so funnels survive URL changes.
//
// This lives here rather than in a React hook because the console runs two
// routers (Pages Router for classic, App Router for `/dashboard`). Hooking
// `history` + `popstate` covers both with one mechanism: every soft navigation
// in either router goes through `pushState`/`replaceState`.
//
// Ordering matters, and is why the hooks are installed at module scope, before
// PostHog's deferred init: PostHog patches the same two methods and adds its
// own `popstate` listener when it initializes, so ours land first and the
// super properties are already current by the time PostHog captures the
// `$pageview` for the new URL. (PostHog's `pushState` wrapper calls the
// previously-installed implementation — ours — before capturing.)

interface ConsoleSurfaceRegistry {
  register: (properties: Record<string, unknown>) => void
  unregister: (property: string) => void
}

declare global {
  interface Window {
    /** Set once `watchConsoleSurface` has installed its hooks. */
    __consoleSurfaceWatched__?: boolean
  }
}

let surfaceRegistry: ConsoleSurfaceRegistry | null = null

const registerConsoleSurface = (): void => {
  // No-op until PostHog is loaded and initialized; `initPostHog` does the
  // first registration itself once it is.
  if (surfaceRegistry == null) return

  const { console_surface, classic_section } = getConsoleSurfaceProperties(
    window.location.pathname
  )

  // Deliberately not deduped by pathname: `register` is a cheap persistence
  // write that emits no event, and re-registering unconditionally is what
  // restores the properties after a `posthog.reset()` clears them.
  try {
    surfaceRegistry.register({ console_surface })
    if (classic_section == null) {
      // Don't let the last classic page's section leak onto dashboard events.
      surfaceRegistry.unregister("classic_section")
    } else {
      surfaceRegistry.register({ classic_section })
    }
  } catch (error) {
    // Analytics must never break navigation.
    console.warn("Could not register console surface properties", error)
  }
}

type HistoryStateMethod = "pushState" | "replaceState"

const watchConsoleSurface = (): void => {
  // Re-evaluating this module (dev HMR) must not stack a second listener and
  // another wrapper layer on `history`.
  if (window.__consoleSurfaceWatched__ === true) return
  window.__consoleSurfaceWatched__ = true

  window.addEventListener("popstate", registerConsoleSurface)

  const patch = (method: HistoryStateMethod): void => {
    const original = window.history[method]
    window.history[method] = function patchedHistoryMethod(
      this: History,
      ...args: Parameters<History[HistoryStateMethod]>
    ): void {
      // Apply the navigation first so `window.location` is already the new URL.
      original.apply(this, args)
      registerConsoleSurface()
    }
  }

  patch("pushState")
  patch("replaceState")
}

const readBootstrapFlags = (): Record<string, boolean | string> | undefined => {
  if (typeof window === "undefined") return undefined
  try {
    const aurora = window.localStorage.getItem(AURORA_FLAG_CACHE_KEY)
    if (aurora === "true") return { aurora_enabled: true }
    if (aurora === "false") return { aurora_enabled: false }
    return undefined
  } catch {
    return undefined
  }
}

const initPostHog = () => {
  import("posthog-js").then(({ default: posthog }) => {
    const bootstrapFlags = readBootstrapFlags()
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      // Required by PostHog for Next.js 15+.
      defaults: "2025-05-24",
      capture_exceptions: true,
      debug: process.env.NODE_ENV === "development",
      session_recording: {
        recordCrossOriginIframes: true,
      },
      ...(bootstrapFlags
        ? { bootstrap: { featureFlags: bootstrapFlags } }
        : {}),
    })

    // Synchronous, so the properties are in place before PostHog's initial
    // `$pageview` (which it captures on a timeout after `init` returns).
    surfaceRegistry = posthog
    registerConsoleSurface()
  })
}

if (typeof window !== "undefined") {
  watchConsoleSurface()

  const isCheckoutReturn =
    new URLSearchParams(window.location.search).get("subscription") ===
    "success"

  if (isCheckoutReturn) {
    initPostHog()
  } else if ("requestIdleCallback" in window) {
    requestIdleCallback(initPostHog, { timeout: 3000 })
  } else {
    setTimeout(initPostHog, 2000)
  }
}
